import React, { useEffect, useState } from 'react';
import {
  Divider,
  Stack,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import { useGetRelatedDPS } from '../api/getRelatedDPS';

import { RelatedDPSListItem } from './RelatedDPSListItem';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { Search } from 'components/Search';
import { useDebouncedCallback } from 'use-debounce';
import { NotFound } from 'components/NotFound/NotFound';
import { useAuth } from 'lib/auth';
import { Loader } from 'components/Loader';
import { CheckboxFilter } from 'components/Filter/Inputs/CheckboxFilter';
import { PremiumBanner } from '../../subscriptions/components/PremiumBanner';

export const DPSTab = ({ id, currentTab, activeCount, expiredCount }) => {
  const dpsTabName = "dps";
  const { account, user } = useAuth();
  // const { id } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    includeInactive: false,
    includeExpired: false,
    sortBy: 'dateCreated',
  });
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);

  const {
    data: { dps, total },
    refetch: refetchDPS,
    isFetching,
  } = useGetRelatedDPS(id, {
    search,
    ...filters,
    page: page - 1,
    limit: pageSize,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      },
      enabled: currentTab === dpsTabName
    },
  });

  const handlePageChange = (_, page) => setPage(page);
  const handleFilterUpdate = ({ target: { name, checked } }) => {
    setFilters((filters) => {
      return { ...filters, [name]: checked };
    });
  };
  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  useTabReset();
  useRefetchDPS();  

  const additionalSilver = activeCount - total;
  const additionalGold = additionalSilver + expiredCount;
  // let messageSilver = `There are ${additionalSilver} more Dynamic Purchasing Systems available for Silver`;
  // if (additionalSilver === 1) {
  //   messageSilver = `There is ${additionalSilver} more Dynamic Purchasing System available for Silver`
  // }

  let messageGold = `There are ${additionalGold} more Dynamic Purchasing Systems available for Premium members`;
  if (additionalGold === 1) {
    messageGold = `There is ${additionalGold} more Dynamic Purchasing System available for Premium members`
  }

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  if (currentTab !== dpsTabName) return null;

  return (
    <>
      <Stack gap={2} height={1}>
        {!account.super_admin && (
          <>
          {(!user.Company.is_premium || user.Company.subscription_level === 'free') && (
            <PremiumBanner sx={{mt:-2, mb:0, ml: -4, width: { xs: 768, lg: 900, xl: 1200 }}} message={`${messageGold}. Click here for more information`} handleSuccess={refetchDPS} currentLevel={user.Company.subscription_level} />
          )}
          </>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} width={1}>
          <Search handleSearchChange={handleSearchChange} sx={{minWidth:200, width: 270}} />
          <Stack direction="row" gap={2} sx={{ flex: '1' }}>
            <CheckboxFilter
              adminOnly
              name="includeInactive"
              onChange={handleFilterUpdate}
              checked={filters.includeInactive}
              label="Inactive"
            />
            <CheckboxFilter
              name="includeExpired"
              onChange={handleFilterUpdate}
              checked={filters.includeExpired}
              label={`Expired (${expiredCount})`}
              subscriptionTypes={['silver', 'gold']}
              tooltip="Available only for Premium members"
            />
          </Stack>
        </Stack>
        <Divider />

        {isFetching && !loading ? 
        (
          <></>
        ) :
        (!isFetching || (isFetching && loading)) ?
        (
          <>
          {loading && (
            <Loader />
          )}

          {!loading && (
            <>
            <Stack gap={1} flex={1}>
              {(!dps?.length || dps?.length === 0) && (
                <NotFound
                    title={`No related DPS found`}
                    caption={
                    search
                        ? 'Please try another search term'
                        : ''
                    }
                    wrapperSx={{ mt: 19 }}
                />
              )}

              {dps.map((d) => (
                <RelatedDPSListItem
                  key={d.dps_id}
                  companyID={id}
                  {...d}
                />
              ))}
            </Stack>

            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
              />
            </Stack>
            </>
          )}
        </>
        ) :
        (
          <></>
        )}
      </Stack>
    </>
  );

   function useRefetchDPS() {
    useEffect(() => {
      refetchDPS();
    }, [page, pageSize, filters, search]);
  }

  function useTabReset() {
    useEffect(() => {
      if (currentTab !== dpsTabName) {
        setPage(1);
        setSearch('');
        handleFilterUpdate({target: {name: "includeInactive", checked: !account.super_admin}});
        handleFilterUpdate({target: {name: "includeExpired", checked: false}});
      }
    }, [currentTab]);
  }
};
